import { computed, ref, watch } from "vue";
import { useToast } from "primevue/usetoast";
import { searchApi } from "@/AlfrescoApi.js";
import {
  extractVariables,
  resolveTemplate,
} from "@/utils/classificationPlanUtils.js";
import { useI18n } from "vue-i18n";

/**
 * Composable for shared classification plan logic between Upload and Requalify popups.
 *
 * @param {Object} options
 * @param {import('vue').Ref<Object>} options.planContent - the loaded plan content
 * @param {import('vue').Ref<Object>} options.metadataValues - reactive metadata field values
 * @param {import('vue').Ref<Object|null>} options.selectedNature - computed selected nature object
 * @param {import('vue').Ref<string|null>} options.destinationNodeName - display name of destination node
 * @param {Object} [options.toast] - optional PrimeVue toast instance (falls back to useToast())
 */
export function useClassificationPlan({
  planContent,
  metadataValues,
  selectedNature,
  destinationNodeName,
  toast: externalToast,
}) {
  let toast;
  try {
    toast = externalToast || useToast();
  } catch {
    // Fallback when called outside setup context (no PrimeVue Toast provider)
    toast = { add: () => {} };
  }
  const { t } = useI18n();

  // --- Search ---
  const searchOptions = ref({});
  const searchLoading = ref({});

  const getSearchOptions = (fieldName) => {
    return searchOptions.value[fieldName] || [];
  };

  const isSearchLoading = (fieldName) => {
    return !!searchLoading.value[fieldName];
  };

  const loadSearchOptions = async (field) => {
    if (!field.search || getSearchOptions(field.name).length > 0) {
      return;
    }

    searchLoading.value[field.name] = true;
    try {
      const query = {
        include: ["properties"],
        query: { language: field.search.lang, query: field.search.query },
      };
      const results = await searchApi.search(query);
      searchOptions.value[field.name] = results.list.entries.map(
        (e) => e.entry.properties[field.search.valueField] || e.entry.name,
      );
    } catch (e) {
      console.error(`Failed to execute search for field ${field.name}`, e);
      toast.add({
        severity: "error",
        summary: t("classificationPlan.toast.searchError"),
        detail: t("classificationPlan.toast.searchFieldFailed", {
          field: field.description,
        }),
        life: 4000,
      });
    } finally {
      searchLoading.value[field.name] = false;
    }
  };

  // --- Validation ---
  const validationErrors = ref({});
  let debounceTimer = null;

  const validateField = (field, value) => {
    if (!field || value === undefined || value === null || value === "") {
      delete validationErrors.value[field.name];
      return;
    }

    if (field.regex) {
      const regex = new RegExp(field.regex);
      if (!regex.test(value)) {
        validationErrors.value[field.name] = t(
          "classificationPlan.validationRegexError",
          { regex: field.regex },
        );
      } else {
        delete validationErrors.value[field.name];
      }
    }
  };

  const debounceValidate = (field, value) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      validateField(field, value);
    }, 500);
  };

  // --- Field helpers ---
  const getFieldDefault = (fieldName) => {
    const field = planContent.value?.FIELDS?.find((f) => f.name === fieldName);
    return field?.default !== undefined && field?.default !== null
      ? field.default
      : undefined;
  };

  // --- Reactive template resolution ---
  const resolveTemplateReactive = (template) => {
    return resolveTemplate(
      template,
      metadataValues.value,
      planContent.value?.FIELDS || [],
    );
  };

  // --- Current fields ---
  const currentFields = computed(() => {
    if (!selectedNature.value || !planContent.value?.FIELDS) {
      return [];
    }
    const requiredFieldSet = new Set();

    Object.values(selectedNature.value.metadata)
      .filter((meta) => meta.field)
      .forEach((meta) => requiredFieldSet.add(meta.field));

    if (selectedNature.value.emplacement && planContent.value.EMPLACEMENT) {
      selectedNature.value.emplacement.forEach((code) => {
        const emp = planContent.value.EMPLACEMENT.find((e) => e.code === code);
        if (emp) {
          if (emp.metadata) {
            Object.values(emp.metadata).forEach((valueObj) => {
              if (valueObj.field) {
                requiredFieldSet.add(valueObj.field);
              }
              if (valueObj.template) {
                extractVariables(valueObj.template).forEach((v) =>
                  requiredFieldSet.add(v),
                );
              }
            });
          }
        }
      });
    }

    const nameTemplate = selectedNature.value.metadata["cm:name"]?.template;
    if (nameTemplate) {
      extractVariables(nameTemplate).forEach((v) => requiredFieldSet.add(v));
    }

    const requiredFieldNames = [...requiredFieldSet];
    return planContent.value.FIELDS.filter((field) =>
      requiredFieldNames.includes(field.name),
    );
  });

  // --- Required fields ---
  const isOptionalField = (fieldName) => {
    const field = planContent.value?.FIELDS?.find((f) => f.name === fieldName);
    return field?.optional === true;
  };

  const requiredFields = computed(() => {
    if (!selectedNature.value) {
      return [];
    }
    const required = new Set();

    Object.entries(selectedNature.value.metadata)
      .filter(([, valueObj]) => valueObj.required && valueObj.field)
      .forEach(([, valueObj]) => required.add(valueObj.field));

    Object.values(selectedNature.value.metadata).forEach((valueObj) => {
      if (valueObj.template) {
        extractVariables(valueObj.template).forEach((v) => required.add(v));
      }
    });

    if (selectedNature.value.emplacement && planContent.value.EMPLACEMENT) {
      selectedNature.value.emplacement.forEach((code) => {
        const emp = planContent.value.EMPLACEMENT.find((e) => e.code === code);
        if (emp) {
          if (emp.metadata) {
            Object.entries(emp.metadata).forEach(([, valueObj]) => {
              if (valueObj.required && valueObj.field) {
                required.add(valueObj.field);
              }
              if (valueObj.template) {
                extractVariables(valueObj.template).forEach((v) =>
                  required.add(v),
                );
              }
            });
          }
        }
      });
    }

    // Remove optional fields — they cannot be required
    return [...required].filter((name) => !isOptionalField(name));
  });

  const isFieldRequired = (fieldName) => {
    return requiredFields.value.includes(fieldName);
  };

  // --- Summary path full (folder hierarchy with types/properties) ---
  const summaryPathFull = computed(() => {
    if (!selectedNature.value || !planContent.value?.EMPLACEMENT) {
      return [];
    }

    return selectedNature.value.emplacement.map((code) => {
      const emp = planContent.value.EMPLACEMENT.find((e) => e.code === code);
      if (!emp) {
        return { name: `[${code}]` };
      }

      const folderNode = {};

      if (emp.type) {
        folderNode.type = emp.type;
      }

      const properties = {};
      let folderName = null;

      if (emp.metadata) {
        for (const [key, valueObj] of Object.entries(emp.metadata)) {
          const type = Object.keys(valueObj).find((k) => k !== "required");
          const value = valueObj[type];

          switch (type) {
            case "value":
              properties[key] = value;
              break;
            case "field": {
              let fieldValue = metadataValues.value[value];
              // Apply default for empty optional fields
              if (
                (fieldValue === undefined ||
                  fieldValue === null ||
                  fieldValue === "") &&
                isOptionalField(value)
              ) {
                const defaultVal = getFieldDefault(value);
                if (defaultVal !== undefined) {
                  fieldValue = defaultVal;
                } else {
                  break;
                }
              }
              properties[key] = fieldValue;
              break;
            }
            case "template":
              properties[key] = resolveTemplateReactive(value);
              break;
            case "parent":
              break;
          }

          if (key === "cm:name" && type !== "parent") {
            switch (type) {
              case "value":
                folderName = value;
                break;
              case "field":
                folderName = metadataValues.value[value];
                break;
              case "template":
                folderName = resolveTemplateReactive(value);
                break;
            }
          }
        }
      }

      if (Object.keys(properties).length > 0) {
        folderNode.properties = properties;
      }

      folderNode.name = folderName || emp.name || `[${code}]`;

      return folderNode;
    });
  });

  // --- Summary destination path (readable string) ---
  const summaryDestinationPath = computed(() => {
    if (!selectedNature.value || !planContent.value?.EMPLACEMENT) {
      return "";
    }
    const parts = selectedNature.value.emplacement.map((code) => {
      const emp = planContent.value.EMPLACEMENT.find((e) => e.code === code);
      if (!emp) {
        return "[emplacement introuvable]";
      }

      if (emp.metadata && emp.metadata["cm:name"]) {
        const nameObj = emp.metadata["cm:name"];
        const type = Object.keys(nameObj).find((k) => k !== "required");
        const value = nameObj[type];

        switch (type) {
          case "value":
            return value;
          case "field":
            return metadataValues.value[value] || `[${value}]`;
          case "template":
            return resolveTemplateReactive(value);
          default:
            return emp.name || `[${code}]`;
        }
      }

      return emp.name || `[${code}]`;
    });
    if (destinationNodeName.value) {
      parts.unshift(destinationNodeName.value);
    }
    return parts.join("/");
  });

  // --- Summary metadata (resolved metadata for the selected nature) ---
  const summaryMetadata = ref({});

  watch(
    [metadataValues, selectedNature],
    () => {
      if (!selectedNature.value) {
        summaryMetadata.value = {};
        return;
      }

      const parentProperties = {};
      summaryPathFull.value.forEach((folder) => {
        if (folder.properties) {
          Object.assign(parentProperties, folder.properties);
        }
      });

      const result = {};
      for (const [key, valueObj] of Object.entries(
        selectedNature.value.metadata,
      )) {
        const type = Object.keys(valueObj).find((k) => k !== "required");
        const value = valueObj[type];
        let resolved;

        switch (type) {
          case "value":
            resolved = value;
            break;
          case "field":
            resolved = metadataValues.value[value];
            // Apply default for empty optional fields
            if (
              (resolved === undefined ||
                resolved === null ||
                resolved === "") &&
              isOptionalField(value)
            ) {
              const defaultVal = getFieldDefault(value);
              if (defaultVal !== undefined) {
                resolved = defaultVal;
              } else {
                continue;
              }
            }
            break;
          case "template":
            resolved = resolveTemplateReactive(value);
            break;
          case "parent":
            if (parentProperties[key]) {
              resolved = parentProperties[key];
            } else {
              continue;
            }
            break;
          default:
            continue;
        }

        result[key] = resolved;
      }
      summaryMetadata.value = result;
    },
    { deep: true },
  );

  // --- Reset helper ---
  const resetClassification = () => {
    searchOptions.value = {};
    searchLoading.value = {};
    validationErrors.value = {};
    summaryMetadata.value = {};
  };

  const dictionaries = computed(() => planContent.value?.DICTIONARIES || []);
  const lexicons = computed(() => planContent.value?.LEXICONS || []);

  return {
    // Fields
    currentFields,
    requiredFields,
    isFieldRequired,
    // Summary
    summaryPathFull,
    summaryDestinationPath,
    summaryMetadata,
    // Template
    resolveTemplateReactive,
    // Search
    searchOptions,
    searchLoading,
    getSearchOptions,
    isSearchLoading,
    loadSearchOptions,
    // Validation
    validationErrors,
    validateField,
    debounceValidate,
    // Dictionaries / Lexicons
    dictionaries,
    lexicons,
    // Reset
    resetClassification,
  };
}
